import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, ShieldAlert, CheckCircle2, Clock, Lock, Loader2, Copy, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { usePaywall } from "@/context/PaywallContext";
import { toast } from "sonner";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
const DEV_SECRET_LS = "titan_dev_paywall_secret";

const PLAN_META = {
  monthly:   { amount: 35,  label: "Monthly",  blurb: "Pay as you go. Cancel anytime." },
  six_month: { amount: 180, label: "6 Months", blurb: "Save 14% vs monthly ($30/mo)." },
  annual:    { amount: 270, label: "Annual",   blurb: "Best value — save 35% ($22.50/mo)." },
};

let _paypalPromise = null;
function loadPayPal() {
  if (window.paypal) return Promise.resolve(window.paypal);
  if (_paypalPromise) return _paypalPromise;
  _paypalPromise = new Promise((resolve, reject) => {
    if (!PAYPAL_CLIENT_ID) {
      _paypalPromise = null;
      return reject(new Error("PayPal Client ID not configured"));
    }
    const existing = document.querySelector('script[data-paypal-sdk]');
    if (existing) {
      existing.addEventListener("load", () => resolve(window.paypal));
      existing.addEventListener("error", () => {
        _paypalPromise = null;
        reject(new Error("PayPal SDK script errored"));
      });
      return;
    }
    const s = document.createElement("script");
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(PAYPAL_CLIENT_ID)}&currency=USD&intent=capture&components=buttons`;
    s.setAttribute("data-paypal-sdk", "1");
    s.async = true;
    s.onload = () => resolve(window.paypal);
    s.onerror = () => {
      _paypalPromise = null;
      reject(new Error("Failed to load PayPal SDK"));
    };
    document.head.appendChild(s);
  });
  return _paypalPromise;
}

function PlanCard({ planId, selected, onSelect }) {
  const meta = PLAN_META[planId];
  return (
    <button
      type="button"
      onClick={() => onSelect(planId)}
      className={`text-left p-5 border-2 transition-all ${selected ? "border-[#e63946] bg-[#e63946]/5" : "border-gray-200 hover:border-gray-400"}`}
      data-testid={`plan-${planId}`}
    >
      <div className="text-[0.65rem] uppercase tracking-widest text-gray-500 font-bold">{meta.label}</div>
      <div className="font-display text-3xl font-bold text-[#0d2040] mt-1">${meta.amount}</div>
      <div className="text-xs text-gray-500 mt-1">{meta.blurb}</div>
    </button>
  );
}

export default function AdminBillingPage() {
  const paywall = usePaywall();
  const [plans, setPlans] = useState([]);
  const [plan, setPlan] = useState("monthly");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submittedOrder, setSubmittedOrder] = useState(null);
  const containerRef = useRef(null);
  const [devSecret, setDevSecret] = useState(() => localStorage.getItem(DEV_SECRET_LS) || "");
  const [showDev, setShowDev] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: pw }, { data: pl }] = await Promise.all([
        api.get("/admin/paywall"),
        api.get("/admin/paywall/plans"),
      ]);
      setData(pw);
      setPlans(pl);
    } catch (e) {
      console.warn("AdminBilling load failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Render PayPal Smart Buttons for the currently-selected plan.
  // Re-run when `loading` flips so the container ref is actually in the DOM.
  useEffect(() => {
    if (loading) return;
    let cancelled = false;
    (async () => {
      if (!containerRef.current) return;
      try {
        const paypal = await loadPayPal();
        if (cancelled || !containerRef.current) return;
        containerRef.current.replaceChildren();
        const meta = PLAN_META[plan];
        await paypal.Buttons({
          style: { layout: "vertical", shape: "rect", label: "paypal", color: "gold" },
          createOrder: (_d, actions) => actions.order.create({
            intent: "CAPTURE",
            purchase_units: [{
              description: `Titan site access — ${meta.label} ($${meta.amount})`,
              amount: { currency_code: "USD", value: meta.amount.toFixed(2) },
            }],
            application_context: { shipping_preference: "NO_SHIPPING", brand_name: "Titan Car Rentals" },
          }),
          onApprove: async (d, actions) => {
            toast.info("Capturing payment…");
            let details = null;
            try { details = await actions.order.capture(); } catch (e) { console.warn("capture failed on client — continuing", e); }
            try {
              const { data: res } = await api.post("/admin/paywall/capture-submission", {
                plan,
                paypal_order_id: d.orderID,
                payer_email: details?.payer?.email_address || null,
                paypal_capture_id: details?.purchase_units?.[0]?.payments?.captures?.[0]?.id || null,
              });
              setSubmittedOrder(res.invoice_id || d.orderID);
              toast.success("Payment submitted! Awaiting developer verification.");
              load();
              paywall.refresh();
            } catch (e) {
              toast.error("Payment captured but server didn't record it. Save the order ID: " + d.orderID);
            }
          },
          onCancel: () => toast.info("Payment cancelled"),
          onError: (err) => {
            console.error("PayPal error", err);
            toast.error("PayPal error — see console");
          },
        }).render(containerRef.current);
      } catch (e) {
        if (!cancelled) toast.error(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [plan, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveDevSecret = () => {
    localStorage.setItem(DEV_SECRET_LS, devSecret.trim());
    toast.success("Developer secret saved locally");
  };

  const markPaid = async (invoiceId) => {
    if (!devSecret) return toast.error("Enter the developer secret first");
    try {
      await api.post("/admin/paywall/mark-paid", { invoice_id: invoiceId }, { headers: { "X-Dev-Secret": devSecret } });
      toast.success("Invoice marked paid — subscription active");
      load();
      paywall.refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mark paid failed");
    }
  };

  const extendGrace = async () => {
    if (!devSecret) return toast.error("Enter the developer secret first");
    try {
      await api.post("/admin/paywall/extend-grace?days=7", null, { headers: { "X-Dev-Secret": devSecret } });
      toast.success("Grace extended by 7 days");
      load();
      paywall.refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Extend grace failed");
    }
  };

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#e63946]" /></div>;

  const statusColor = {
    active: "bg-emerald-50 border-emerald-300 text-emerald-700",
    grace: "bg-amber-50 border-amber-300 text-amber-800",
    suspended: "bg-red-50 border-red-300 text-red-700",
  }[data?.status || "grace"];

  return (
    <div className="p-6 sm:p-10 max-w-5xl mx-auto" data-testid="admin-billing-page">
      <span className="eyebrow">Subscription</span>
      <h1 className="font-display text-3xl font-bold text-[#0d2040] mt-2">Billing & Service Status</h1>
      <p className="text-gray-500 text-sm mt-1">
        Your Titan Car Rentals site is covered by a developer service subscription. Pay to keep the site live.
      </p>

      {/* Status card */}
      <div className={`mt-6 p-5 border-l-4 ${statusColor}`} data-testid="billing-status-card">
        <div className="flex items-start gap-3">
          {data?.status === "active" ? <CheckCircle2 className="shrink-0 mt-0.5" /> : data?.status === "suspended" ? <ShieldAlert className="shrink-0 mt-0.5" /> : <Clock className="shrink-0 mt-0.5" />}
          <div>
            <div className="font-bold uppercase tracking-widest text-sm">
              {data?.status === "active" && "Active"}
              {data?.status === "grace" && `Grace period — ${data.grace_days_left} day${data.grace_days_left === 1 ? "" : "s"} remaining`}
              {data?.status === "suspended" && "Service suspended — payment required"}
            </div>
            <div className="text-xs mt-1 opacity-80">
              {data?.plan ? `Current plan: ${PLAN_META[data.plan]?.label}` : "No active plan yet."}
              {" · "}Period ends: {new Date(data?.period_end).toLocaleDateString()}
              {" · "}Grace ends: {new Date(data?.grace_end).toLocaleDateString()}
            </div>
          </div>
        </div>
      </div>

      {/* Plan picker */}
      <div className="mt-10">
        <h2 className="font-display text-xl font-bold text-[#0d2040]">Choose a plan</h2>
        <div className="grid sm:grid-cols-3 gap-3 mt-4">
          {Object.keys(PLAN_META).map((p) => (
            <PlanCard key={p} planId={p} selected={plan === p} onSelect={setPlan} />
          ))}
        </div>
        <div className="mt-6 p-5 border border-gray-200 bg-white">
          <div className="flex items-center gap-2 mb-3 text-sm text-gray-600">
            <CreditCard size={16} /> Pay with PayPal
          </div>
          <div ref={containerRef} data-testid="paypal-button-container" />
          {submittedOrder && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-300 text-xs text-amber-900">
              Order <strong>{submittedOrder}</strong> submitted. Your developer will confirm payment and unlock the site shortly.
            </div>
          )}
        </div>
      </div>

      {/* Invoice history */}
      <div className="mt-10">
        <h2 className="font-display text-xl font-bold text-[#0d2040]">Payment history</h2>
        <div className="mt-4 bg-white border border-gray-200">
          {(data?.invoices || []).length === 0 ? (
            <div className="p-5 text-sm text-gray-500">No invoices yet.</div>
          ) : (
            <table className="w-full text-sm" data-testid="invoice-table">
              <thead className="bg-gray-50 text-[0.65rem] uppercase tracking-widest text-gray-500">
                <tr>
                  <th className="text-left p-3">Invoice</th>
                  <th className="text-left p-3">Plan</th>
                  <th className="text-left p-3">Amount</th>
                  <th className="text-left p-3">PayPal order</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3"></th>
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((inv) => (
                  <tr key={inv.id} className="border-t border-gray-200" data-testid={`invoice-row-${inv.id}`}>
                    <td className="p-3 font-mono text-xs">{inv.id}</td>
                    <td className="p-3">{PLAN_META[inv.plan]?.label || inv.plan}</td>
                    <td className="p-3">${inv.amount}</td>
                    <td className="p-3 font-mono text-[0.65rem]">
                      {inv.paypal_order_id ? (
                        <>
                          {inv.paypal_order_id.slice(0, 10)}…
                          <button onClick={() => { navigator.clipboard.writeText(inv.paypal_order_id); toast.success("Copied"); }} className="ml-1 text-gray-400 hover:text-[#e63946]"><Copy size={10} /></button>
                        </>
                      ) : "—"}
                    </td>
                    <td className="p-3">
                      <span className={`inline-block px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-widest ${inv.status === "paid" ? "bg-emerald-100 text-emerald-700" : inv.status === "void" ? "bg-gray-200 text-gray-500" : "bg-amber-100 text-amber-800"}`}>
                        {inv.status.replace("_", " ")}
                      </span>
                    </td>
                    <td className="p-3 text-xs text-gray-500">{new Date(inv.created_at).toLocaleString()}</td>
                    <td className="p-3">
                      {inv.status !== "paid" && showDev && (
                        <button
                          onClick={() => markPaid(inv.id)}
                          className="text-xs text-[#e63946] font-bold uppercase hover:underline"
                          data-testid={`mark-paid-${inv.id}`}
                        >Mark paid</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Developer tools (hidden behind secret) */}
      <div className="mt-14 pt-6 border-t border-gray-200">
        <button
          onClick={() => setShowDev((v) => !v)}
          className="text-[0.65rem] uppercase tracking-widest text-gray-400 hover:text-[#0d2040] flex items-center gap-2"
          data-testid="toggle-dev-tools"
        >
          <Lock size={10} /> {showDev ? "Hide" : "Show"} developer tools
        </button>
        {showDev && (
          <div className="mt-4 p-5 border border-dashed border-gray-300 bg-gray-50 space-y-3">
            <div className="text-xs text-gray-600">Only the developer has this secret. It&apos;s stored only in this browser.</div>
            <div className="flex gap-2">
              <input
                type="password"
                value={devSecret}
                onChange={(e) => setDevSecret(e.target.value)}
                placeholder="X-Dev-Secret"
                className="titan-input titan-input--mobile flex-1"
                data-testid="dev-secret-input"
              />
              <button onClick={saveDevSecret} className="btn-primary px-5 text-xs uppercase" data-testid="save-dev-secret">Save</button>
            </div>
            <button onClick={extendGrace} className="text-xs text-[#e63946] font-bold uppercase hover:underline" data-testid="extend-grace-btn">
              + Extend grace by 7 days
            </button>
          </div>
        )}
      </div>

      <div className="mt-10 text-xs text-gray-400">
        <Link to="/admin" className="hover:text-[#0d2040]">← Back to admin</Link>
      </div>
    </div>
  );
}
